import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IAgoraRTCClient,
  ILocalAudioTrack,
  IAgoraRTCRemoteUser,
  createClient,
  setLogLevel,
  createMicrophoneAudioTrack,
  ILocalTrack,
} from 'agora-rtc-sdk-ng/esm';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { APP_ROUTES } from '@navigation/types';
import socket from '@services/socket.service';
import { useShowDataById, useCurrentShowId } from '@redux/selectors/shows';
import { useCurrentUserParticipantData } from '@redux/selectors/participants';
import { AppDispatch } from '@redux/store';
import {
  TeamType,
  ParticipantRole,
  ParticipantPosition,
  ParticipantStatus,
  UpdateParticipantRoleSocketData,
} from '@typings/participant';
import { participantsSlice } from '@redux/state/participantsSlice';
import { showsSlice } from '@redux/state/showsSlice';
import LiveShowMiniPlayer from '@components/miniPlayer/LiveShowMiniPlayer';
import {
  useJoinToShowMutation,
  useLazyGetShowByIdQuery,
  useFinishShowMutation,
} from '@redux/queries/show';
import { AlertType } from '@utils/constants';
import { topicsSlice } from '@redux/state/topicsSlice';
import { chatSlice } from '@redux/state/chatSlice';
import { useLeftTeamSpeakerId } from '@redux/selectors/speakers';
import { useRightTeamSpeakerId } from '@redux/selectors/speakers';
import { useSpeakerDataById } from '@redux/selectors/speakers';
import { ShowStatus } from '@typings/show';
import { RequestToSpeakStatus } from '@typings/requestToSpeak';
import { requestsToSpeakSlice } from '@redux/state/requestsToSpeakSlice';
import { useParticipantRequestToSpeak } from '@redux/selectors/requestsToSpeak';
import { useAuthUser } from '@redux/selectors/auth';
import { useLeaveShowMutation } from '@redux/queries/participant';

import { useAlert } from '../AlertProvider';

import { useSocketHandlers } from './socketHandlers';

type AgoraEngineContextType = {
  currentShowId: string;
  isJoined: boolean;
  isHost: boolean;
  isCoHost: boolean;
  isReadyToSpeak: boolean;
  setIsReadyToSpeak: (isReady: boolean) => void;
  leaveAgoraChannel: () => void;
  onLeaveShowCompletely: () => void;
  onLeaveShowAsSpeakerOrListener: () => void;
  onFinishShow: () => void;
  onFinishOrLeaveCurrentShow: () => void;
  resetCurrentShowState: () => void;
  toggleMute: (muted: boolean) => void;
};

type AgoraEngineContextProvider = {
  children: ReactNode;
};

const enum SocketConnectionState {
  Init = 'init',
  Connected = 'connected',
  Reconnected = 'reconnected',
  Disconnected = 'disconnected',
  Error = 'error',
}

const appId = process.env.REACT_APP_AGORA_APP_ID || '';
const token = null;

export const AgoraEngineContext = createContext<AgoraEngineContextType>({
  currentShowId: '',
  isJoined: false,
  isHost: false,
  isCoHost: false,
  isReadyToSpeak: false,
  setIsReadyToSpeak: () => {},
  leaveAgoraChannel: () => {},
  onLeaveShowCompletely: () => {},
  onLeaveShowAsSpeakerOrListener: () => {},
  onFinishShow: () => {},
  onFinishOrLeaveCurrentShow: () => {},
  resetCurrentShowState: () => {},
  toggleMute: () => {},
});

export const useAgoraEngine = () => useContext(AgoraEngineContext);

export const AgoraEngineProvider: FC<AgoraEngineContextProvider> = ({
  children,
}) => {
  const agoraRef = useRef<IAgoraRTCClient | null>(null);
  const localAudioTrackRef = useRef<ILocalAudioTrack | null>(null);
  const [isReadyToConnectToAgora, setIsReadyToConnectToAgora] =
    useState<boolean>(false);
  const [isJoined, setIsJoined] = useState<boolean>(false);
  const [isAudioOn, setIsAudioOn] = useState<boolean>(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [isReadyToSpeak, setIsReadyToSpeak] = useState(false);
  const [socketConnectionState, setSocketConnectionState] =
    useState<SocketConnectionState>(SocketConnectionState.Init);

  const currentShowId = useCurrentShowId();
  const show = useShowDataById(currentShowId);
  const { user: authUser, isAnonymous } = useAuthUser();
  const participant = useCurrentUserParticipantData(currentShowId);
  const rightSpeakerId = useRightTeamSpeakerId(currentShowId);
  const leftSpeakerId = useLeftTeamSpeakerId(currentShowId);
  const rightSpeakerProfileId = useSpeakerDataById(rightSpeakerId)?.profileId;
  const leftSpeakerProfileId = useSpeakerDataById(leftSpeakerId)?.profileId;
  const participantRequest = useParticipantRequestToSpeak(currentShowId);
  const isHost = useMemo(
    () => !!show?.id && show?.hostId === authUser?.id,
    [show?.id, show?.hostId, authUser?.id],
  );
  const isCoHost = useMemo(
    () => !!show?.id && show?.coHostId === authUser?.id,
    [show?.id, show?.coHostId, authUser],
  );

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  const [
    joinToShow,
    { isSuccess: isJoinSuccess, isError: isJoinError, reset: resetJoinToShow },
  ] = useJoinToShowMutation();
  const [finishShow] = useFinishShowMutation();
  const [leaveShow] = useLeaveShowMutation();
  const [leaveShowAsSpeaker] = useLeaveShowMutation();
  const [getShowById] = useLazyGetShowByIdQuery();
  // const [getRequestsToSpeakCount] =
  //   useLazyGetRequestsToSpeakCountByShowIdQuery();

  const turnOnMicrophone = useCallback(
    async (enabled?: boolean) => {
      const shouldEnable = enabled ?? !isAudioOn;
      setIsAudioOn(shouldEnable);

      try {
        if (localAudioTrackRef.current) {
          await localAudioTrackRef.current.setEnabled(shouldEnable);
        } else {
          const audioTrack = await createMicrophoneAudioTrack();
          await audioTrack.setEnabled(shouldEnable);
          localAudioTrackRef.current = audioTrack;
        }
      } catch (error) {
        console.log('Error managing microphone:', error);
        localAudioTrackRef.current = null;
        const audioTrack = await createMicrophoneAudioTrack();
        await audioTrack.setEnabled(shouldEnable);
        localAudioTrackRef.current = audioTrack;
      }
    },
    [isAudioOn],
  );

  const onClientRoleChanged = useCallback(
    (role: ParticipantRole) => {
      if (isHost || isCoHost) {
        return;
      }

      if (role === ParticipantRole.Speaker) {
        turnOnMicrophone(true);
        agoraRef.current?.setClientRole('host');
      } else {
        turnOnMicrophone(false);
        agoraRef.current?.setClientRole('audience');
      }
    },
    [turnOnMicrophone, isHost, isCoHost],
  );

  const resetCurrentShowState = useCallback(() => {
    dispatch(showsSlice.actions.setCurrentShowId(null));
    dispatch(chatSlice.actions.clearChatMessages());
    setIsJoined(false);
    setIsReadyToConnectToAgora(false);
    setRemoteUsers([]);
    setIsAudioOn(false);
    setIsReadyToSpeak(false);
    resetJoinToShow();
  }, [dispatch, resetJoinToShow]);

  const initAgoraClient = useCallback(async () => {
    if (isJoined) {
      return;
    }

    // 2: WARNING. Output logs of the WARNING and ERROR level
    setLogLevel(2);
    const client = createClient({ mode: 'live', codec: 'vp8' });
    agoraRef.current = client;

    client.setClientRole(isHost || isCoHost ? 'host' : 'audience');

    client.on('user-published', async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      if (mediaType === 'audio') {
        user.audioTrack?.play();
      }
      setRemoteUsers((prev) => [...prev, user]);
    });

    client.on('user-unpublished', (user, mediaType) => {
      if (mediaType === 'audio') {
        user.audioTrack?.stop();
      }
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
    });

    await client.join(appId, show?.id, token, participant?.uid);

    if (isHost || isCoHost) {
      await turnOnMicrophone(true);
      await client.publish([localAudioTrackRef.current as ILocalTrack]);
    }

    setIsJoined(true);
  }, [
    isJoined,
    show?.id,
    participant?.uid,
    isHost,
    isCoHost,
    turnOnMicrophone,
  ]);

  const leaveAgoraChannel = useCallback(async () => {
    try {
      localAudioTrackRef.current?.stop();
      localAudioTrackRef.current?.close();
      localAudioTrackRef.current = null;
      await agoraRef.current?.leave();
      agoraRef.current?.removeAllListeners();
      agoraRef.current = null;
    } catch (error) {
      console.error('Leave agora channel error:', error);
    }
  }, []);

  const onFinishShow = useCallback(() => {
    try {
      leaveAgoraChannel();
      finishShow({ id: show?.id });
    } catch (error) {
      console.log('Finish show error', error);
    }
  }, [leaveAgoraChannel, finishShow, show?.id]);

  const onLeaveShowCompletely = useCallback(() => {
    try {
      if (participant?.id) {
        leaveShow({
          id: participant?.id,
          status: ParticipantStatus.Left,
          isAnonymous,
        });
      }
      leaveAgoraChannel();
      resetCurrentShowState();
      navigate(APP_ROUTES.HOME, { replace: true });
    } catch (error) {
      console.log('Leave show completely error', error);
    }
  }, [
    isAnonymous,
    participant?.id,
    leaveShow,
    leaveAgoraChannel,
    resetCurrentShowState,
    navigate,
  ]);

  const onLeaveShowAsSpeakerOrListener = useCallback(() => {
    if (participant?.id) {
      if (
        rightSpeakerProfileId === participant.profileId ||
        leftSpeakerProfileId === participant.profileId
      ) {
        onClientRoleChanged(ParticipantRole.Listener);
        leaveShowAsSpeaker({
          id: participant.id,
          status: ParticipantStatus.Muted,
        });
        return;
      }

      onLeaveShowCompletely();
    }
  }, [
    participant?.id,
    participant?.profileId,
    leftSpeakerProfileId,
    rightSpeakerProfileId,
    onClientRoleChanged,
    leaveShowAsSpeaker,
    onLeaveShowCompletely,
  ]);

  const onFinishOrLeaveCurrentShow = useCallback(() => {
    if (currentShowId) {
      if (isHost) {
        onFinishShow();
        resetCurrentShowState();
      } else {
        onLeaveShowCompletely();
      }
    }
  }, [
    currentShowId,
    isHost,
    onFinishShow,
    onLeaveShowCompletely,
    resetCurrentShowState,
  ]);

  const getSocketHandlers = useSocketHandlers({
    dispatch,
    user: authUser?.id ? { id: authUser?.id } : null,
    show: show?.id ? { id: show?.id } : null,
    rightSpeakerId,
    leftSpeakerId,
    onClientRoleChanged,
    turnOnMicrophone,
    isHost,
    isCoHost,
  });

  const socketHandlers = show?.id ? getSocketHandlers() : null;

  useEffect(() => {
    if (
      show?.id &&
      show?.status !== ShowStatus.Finished &&
      !isJoined &&
      !isHost
    ) {
      if (isAnonymous && authUser?.id) {
        joinToShow({
          showId: show?.id,
          isAnonymous,
          token: authUser?.id,
        });
      } else {
        joinToShow({ showId: show?.id });
      }
    }
  }, [
    isJoined,
    isHost,
    show?.id,
    show?.status,
    joinToShow,
    isAnonymous,
    authUser?.id,
  ]);

  useEffect(() => {
    if (isJoinSuccess || isHost) {
      setIsReadyToConnectToAgora(true);
    }
  }, [isJoinSuccess, isHost]);

  useEffect(() => {
    if (isJoinError) {
      showAlert(AlertType.Error, 'Failed to join the show. Please try again.');
    }
  }, [isJoinError, showAlert]);

  useEffect(() => {
    if (!show?.id) {
      return;
    }

    if (
      show?.id &&
      show?.status !== ShowStatus.Finished &&
      participant?.uid &&
      !isJoined &&
      isReadyToConnectToAgora
    ) {
      initAgoraClient();
    }
  }, [
    show?.id,
    show?.status,
    participant?.uid,
    isJoined,
    initAgoraClient,
    isReadyToConnectToAgora,
  ]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('socket connected', socket.connected);
      setSocketConnectionState((prevState) =>
        prevState === SocketConnectionState.Init
          ? SocketConnectionState.Connected
          : SocketConnectionState.Reconnected,
      );
    });

    return () => {
      socket.off('connect');
    };
  }, []);

  useEffect(() => {
    socket.on('connect_error', () => {
      console.log('socket connect_error', socket.connected);
      setSocketConnectionState(SocketConnectionState.Error);
      setTimeout(() => {
        socket.connect();
      }, 1000);
    });

    return () => {
      socket.off('connect_error');
    };
  }, []);

  useEffect(() => {
    socket.on('socket disconnect', (reason, description) => {
      console.log('disconnect', socket.connected, reason, description);
      setSocketConnectionState(SocketConnectionState.Disconnected);

      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        socket.connect();
      }
    });

    return () => {
      socket.off('disconnect');
    };
  }, []);

  useEffect(() => {
    if (socketConnectionState === 'reconnected' && show?.id) {
      if (socketConnectionState === 'reconnected') {
        setSocketConnectionState(SocketConnectionState.Connected);

        if (show?.id && isJoined) {
          getShowById({ id: show?.id, _ts: new Date().getTime() }, false);
          // if (isHost) {
          //   getRequestsToSpeakCount({ showId: show?.id });
          // }
        }
      }
    }
  }, [socketConnectionState, show?.id, getShowById, isJoined]);

  useEffect(() => {
    if (
      socketConnectionState === SocketConnectionState.Error ||
      socketConnectionState === SocketConnectionState.Disconnected
    ) {
      showAlert(
        AlertType.Error,
        `${
          socketConnectionState === SocketConnectionState.Error
            ? 'Connection error'
            : 'Disconnected'
        }. Trying to reconnect...`,
      );
    }
  }, [socketConnectionState, showAlert]);

  useEffect(() => {
    if (!show?.id || !socketHandlers) {
      return;
    }

    socket.on(
      `join-to-show-${show?.id}`,
      isAnonymous && authUser?.id
        ? socketHandlers.handleAnonymousParticipantJoin
        : socketHandlers.handleParticipantJoin,
    );

    return () => {
      socket.off(`join-to-show-${show?.id}`);
    };
  }, [show?.id, socketHandlers, isAnonymous, authUser?.id]);

  useEffect(() => {
    if (!show?.id || !socketHandlers) {
      return;
    }

    socket.on(`leave-show-${show?.id}`, socketHandlers.handleParticipantLeave);

    return () => {
      socket.off(`leave-show-${show?.id}`);
    };
  }, [show.id, socketHandlers]);

  useEffect(() => {
    if (!show?.id) {
      return;
    }

    socket.on(
      `update-participant-role-${show?.id}`,
      (participants: UpdateParticipantRoleSocketData[]) => {
        participants.forEach((part) => {
          if (part.role === ParticipantRole.Speaker) {
            const position =
              part.speaker.side === TeamType.Blue
                ? ParticipantPosition.Left
                : ParticipantPosition.Right;
            let animation;
            if (
              part.profile.id !== leftSpeakerProfileId &&
              part.profile.id !== rightSpeakerProfileId
            ) {
              // const leftProfileId =
              //   position === ParticipantPosition.Left
              //     ? part.profile.id
              //     : leftSpeakerProfileId;
              // const rightProfileId =
              //   position === ParticipantPosition.Right
              //     ? part.profile.id
              //     : rightSpeakerProfileId;
              // animation =
              //   position === ParticipantPosition.Left &&
              //   (!rightSpeakerProfileId || show.type === ShowType.Sideless)
              //     ? `${Animation.NewLeftSpeaker}_${part.profile.id}`
              //     : position === ParticipantPosition.Right &&
              //         (!leftSpeakerProfileId || show.type === ShowType.Sideless)
              //       ? `${Animation.NewRightSpeaker}_${part.profile.id}`
              //       : `${Animation.VS}_${leftProfileId}_${rightProfileId}`;
            }

            if (
              participantRequest &&
              participantRequest?.id === part.requestId
            ) {
              dispatch(
                requestsToSpeakSlice.actions.setRequestToSpeakDataById({
                  id: participantRequest.id,
                  status: RequestToSpeakStatus.Fulfilled,
                }),
              );
            }

            socketHandlers?.handleSpeakerDataUpdate(part);

            dispatch(
              showsSlice.actions.setShowDataById({
                [`${position}TeamSpeakerId`]: part.speaker.id,
                id: show?.id,
              }),
            );

            if (animation) {
              // addAnimation(animation);
            }

            if (
              participant?.id === part.id &&
              participant?.role !== ParticipantRole.Speaker
            ) {
              if (
                !!location.pathname &&
                !location.pathname.includes(APP_ROUTES.SHOW_LIVE)
              ) {
                navigate(`${APP_ROUTES.SHOW_LIVE}/${show?.id}`);
                if (animation) {
                  // addAnimation(animation);
                }
              }

              // setIsCountdownShown(true);
            }
            return;
          }

          if (part.role === ParticipantRole.Listener) {
            dispatch(
              participantsSlice.actions.setParticipantDataById({
                id: part.id,
                role: part.role,
                status: part.status,
              }),
            );
            return;
          }
        });
      },
    );

    return () => {
      socket.off(`update-participant-role-${show?.id}`);
    };
  }, [
    show?.id,
    show.type,
    dispatch,
    participantRequest,
    participant?.id,
    participant?.role,
    location.pathname,
    navigate,
    // addAnimation,
    leftSpeakerProfileId,
    rightSpeakerProfileId,
    socketHandlers,
  ]);

  useEffect(() => {
    if (participant?.role && isJoined) {
      onClientRoleChanged(participant.role as ParticipantRole);
    }
  }, [participant?.role, isJoined, onClientRoleChanged]);

  useEffect(() => {
    if (!show?.id || !socketHandlers) {
      return;
    }

    socket.on(`update-show-${show?.id}`, socketHandlers?.handleShowUpdate);

    return () => {
      socket.off(`update-show-${show?.id}`);
    };
  }, [show?.id, socketHandlers]);

  useEffect(() => {
    if (!show?.id || !socketHandlers) {
      return;
    }

    socket.on(
      `update-participant-status-${show?.id}`,
      socketHandlers?.handleParticipantStatusUpdate,
    );

    return () => {
      socket.off(`update-participant-status-${show?.id}`);
    };
  }, [show?.id, socketHandlers]);

  useEffect(() => {
    if (!show?.id || !socketHandlers) {
      return;
    }

    socket.on(`end-show-${show?.id}`, socketHandlers?.handleEndShow);

    return () => {
      socket.off(`end-show-${show?.id}`);
    };
  }, [show.id, socketHandlers]);

  useEffect(() => {
    if ((!show?.id && !isCoHost) || !socketHandlers) {
      return;
    }

    socket.on(
      `end-show-coHost-${show?.id}`,
      socketHandlers?.handleEndShowCoHost,
    );

    return () => {
      socket.off(`end-show-coHost-${show?.id}`);
    };
  }, [show?.id, isCoHost, socketHandlers]);

  useEffect(() => {
    if (!show?.id) {
      return;
    }

    let votesBuffer = { left: 0, right: 0 };
    let topicId = '';

    socket.on(
      `votes-show-${show?.id}`,
      (payload: { topicId: string; left: number; right: number }) => {
        topicId = payload.topicId;
        votesBuffer = {
          left: payload.left,
          right: payload.right,
        };
      },
    );

    const interval = setInterval(() => {
      if (votesBuffer.left || votesBuffer.right) {
        dispatch(
          topicsSlice.actions.setTopicDataById({
            id: topicId,
            leftVotes: votesBuffer.left,
            rightVotes: votesBuffer.right,
          }),
        );
        votesBuffer = { left: 0, right: 0 };
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      socket.off(`votes-show-${show?.id}`);
    };
  }, [show?.id, dispatch]);

  useEffect(() => {
    console.log('remoteUsers', remoteUsers);
  }, [remoteUsers]);

  return (
    <AgoraEngineContext.Provider
      value={{
        currentShowId,
        isJoined,
        isHost,
        isCoHost,
        isReadyToSpeak,
        setIsReadyToSpeak,
        leaveAgoraChannel,
        onLeaveShowCompletely,
        onLeaveShowAsSpeakerOrListener,
        onFinishShow,
        onFinishOrLeaveCurrentShow,
        resetCurrentShowState,
        toggleMute: turnOnMicrophone,
      }}
    >
      {children}

      <LiveShowMiniPlayer />
    </AgoraEngineContext.Provider>
  );
};
